import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

/**
 * パーセントの入力欄の値を、保存してよい数に直す。
 *
 * 欄を空にすると parseFloat('') が NaN になり、これがそのまま
 * Firestore に入っていた。画面はエラーを出さず成功と表示する。
 * 0〜100 の外や数値でないものは、既定値に倒す。
 */
function pct(v: number, fallback: number): number {
  if (!Number.isFinite(v)) return fallback;
  if (v < 0 || v > 100) return fallback;
  return v;
}

const MobileOrderSettings: React.FC = () => {
  const { shop } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // モバイルオーダー設定
  const [mobileOrderEnabled, setMobileOrderEnabled] = useState(true);
  const [defaultLanguage, setDefaultLanguage] = useState<'ja' | 'th' | 'en' | 'zh-TW' | 'ko'>('ja');
  const [priceDisplayType, setPriceDisplayType] = useState<'tax_included' | 'tax_excluded'>('tax_included');
  const [taxRate, setTaxRate] = useState<number>(10);
  const [reducedTaxRate, setReducedTaxRate] = useState<number>(8);
  const [serviceCharge, setServiceCharge] = useState<number>(0);
  const [creditCardFee, setCreditCardFee] = useState<number>(0);

  // 画像表示設定
  const [displaySettings, setDisplaySettings] = useState({
    showCategoryImages: true,
    showProductImages: true,
    showOptionImages: true,
  });

  // テーマカラー設定
  const [themeSettings, setThemeSettings] = useState({
    backgroundColor: '#ffffff',
    textColor: '#333333',
    primaryColor: '#1976d2',
    secondaryColor: '#9c27b0',
    headerBackgroundColor: '#1976d2',
    headerTextColor: '#ffffff',
    buttonColor: '#1976d2',
    buttonTextColor: '#ffffff',
    categoryBackgroundColor: '#f5f5f5',
    categoryTextColor: '#333333',
  });

  // デフォルトテーマカラー
  const defaultThemeSettings = {
    backgroundColor: '#ffffff',
    textColor: '#333333',
    primaryColor: '#1976d2',
    secondaryColor: '#9c27b0',
    headerBackgroundColor: '#1976d2',
    headerTextColor: '#ffffff',
    buttonColor: '#1976d2',
    buttonTextColor: '#ffffff',
    categoryBackgroundColor: '#f5f5f5',
    categoryTextColor: '#333333',
  };

  useEffect(() => {
    if (shop) {
      // モバイルオーダー有効/無効設定
      setMobileOrderEnabled(shop.mobileOrderEnabled !== false);

      // 言語・価格表示設定
      setDefaultLanguage(shop.defaultLanguage || 'ja');
      setPriceDisplayType(shop.priceDisplayType || 'tax_included');
      setTaxRate(shop.taxRate !== undefined ? Math.round(shop.taxRate * 1000) / 10 : 10);
      setReducedTaxRate(shop.reducedTaxRate !== undefined ? Math.round(shop.reducedTaxRate * 1000) / 10 : 8);
      setServiceCharge(shop.serviceCharge !== undefined ? Math.round(shop.serviceCharge * 1000) / 10 : 0);
      setCreditCardFee(shop.creditCardFee !== undefined ? Math.round(shop.creditCardFee * 1000) / 10 : 0);

      // 画像表示設定
      if (shop.displaySettings) {
        setDisplaySettings({
          showCategoryImages: shop.displaySettings.showCategoryImages !== false,
          showProductImages: shop.displaySettings.showProductImages !== false,
          showOptionImages: shop.displaySettings.showOptionImages !== false,
        });
      }

      // テーマカラー設定
      if (shop.themeSettings) {
        setThemeSettings({
          backgroundColor: shop.themeSettings.backgroundColor || defaultThemeSettings.backgroundColor,
          textColor: shop.themeSettings.textColor || defaultThemeSettings.textColor,
          primaryColor: shop.themeSettings.primaryColor || defaultThemeSettings.primaryColor,
          secondaryColor: shop.themeSettings.secondaryColor || defaultThemeSettings.secondaryColor,
          headerBackgroundColor: shop.themeSettings.headerBackgroundColor || defaultThemeSettings.headerBackgroundColor,
          headerTextColor: shop.themeSettings.headerTextColor || defaultThemeSettings.headerTextColor,
          buttonColor: shop.themeSettings.buttonColor || defaultThemeSettings.buttonColor,
          buttonTextColor: shop.themeSettings.buttonTextColor || defaultThemeSettings.buttonTextColor,
          categoryBackgroundColor: shop.themeSettings.categoryBackgroundColor || defaultThemeSettings.categoryBackgroundColor,
          categoryTextColor: shop.themeSettings.categoryTextColor || defaultThemeSettings.categoryTextColor,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shop) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const shopRef = doc(db, 'shops', shop.id);

      await updateDoc(shopRef, {
        mobileOrderEnabled,
        defaultLanguage,
        priceDisplayType,
        // 欄を空にすると parseFloat('') が NaN になり、そのまま Firestore に入る。
        // 「標準税率 (NaN%)」と表示されるのに請求は 10% で計算される、という
        // 表示と金額の食い違いになっていた。数値でなければ保存しない。
        taxRate: pct(taxRate, 10) / 100,
        reducedTaxRate: pct(reducedTaxRate, 8) / 100,
        serviceCharge: pct(serviceCharge, 0) / 100,
        creditCardFee: pct(creditCardFee, 0) / 100,
        displaySettings: {
          showCategoryImages: displaySettings.showCategoryImages,
          showProductImages: displaySettings.showProductImages,
          showOptionImages: displaySettings.showOptionImages,
        },
        themeSettings: themeSettings,
        updatedAt: Timestamp.now(),
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating mobile order settings:', err);
      setError('モバイルオーダー設定の更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (!shop) {
    return (
      <Container>
        <Typography>店舗情報を読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          モバイルオーダー設定
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            設定を保存しました
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* モバイルオーダー有効/無効 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              基本設定
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={mobileOrderEnabled}
                  onChange={(e) => setMobileOrderEnabled(e.target.checked)}
                />
              }
              label="モバイルオーダーを有効にする"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 言語・価格表示設定 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              言語・価格表示設定
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="デフォルト言語"
                  value={defaultLanguage}
                  onChange={(e) => setDefaultLanguage(e.target.value as any)}
                  helperText="顧客ページのデフォルト表示言語"
                >
                  <MenuItem value="ja">日本語</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="th">ภาษาไทย (Thai)</MenuItem>
                  <MenuItem value="zh-TW">繁體中文 (Traditional Chinese)</MenuItem>
                  <MenuItem value="ko">한국어 (Korean)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="価格表示タイプ"
                  value={priceDisplayType}
                  onChange={(e) => setPriceDisplayType(e.target.value as any)}
                  helperText="税込/税抜表示の選択"
                >
                  <MenuItem value="tax_included">税込表示</MenuItem>
                  <MenuItem value="tax_excluded">税抜表示</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="標準税率 (%)"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText="例: 10% の場合は 10 と入力"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="軽減税率 (%)"
                  value={reducedTaxRate}
                  onChange={(e) => setReducedTaxRate(parseFloat(e.target.value))}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText="例: 8% の場合は 8 と入力"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="サービス料 (%)"
                  value={serviceCharge}
                  onChange={(e) => setServiceCharge(parseFloat(e.target.value))}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText="例: 10% の場合は 10 と入力"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="クレジットカード手数料 (%)"
                  value={creditCardFee}
                  onChange={(e) => setCreditCardFee(parseFloat(e.target.value))}
                  inputProps={{ step: 0.1, min: 0, max: 100 }}
                  helperText="例: 3.6% の場合は 3.6 と入力"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 画像表示設定 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              画像表示設定
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              顧客向けページでの画像表示の有効/無効を設定します
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={displaySettings.showCategoryImages}
                    onChange={(e) => setDisplaySettings({
                      ...displaySettings,
                      showCategoryImages: e.target.checked
                    })}
                  />
                }
                label="カテゴリ画像を表示する"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={displaySettings.showProductImages}
                    onChange={(e) => setDisplaySettings({
                      ...displaySettings,
                      showProductImages: e.target.checked
                    })}
                  />
                }
                label="商品画像を表示する"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={displaySettings.showOptionImages}
                    onChange={(e) => setDisplaySettings({
                      ...displaySettings,
                      showOptionImages: e.target.checked
                    })}
                  />
                }
                label="オプション画像を表示する"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* テーマカラー設定 */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                テーマカラー設定
              </Typography>
              <Tooltip title="デフォルトに戻す">
                <IconButton
                  size="small"
                  onClick={() => setThemeSettings(defaultThemeSettings)}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              モバイルオーダー画面の色をカスタマイズできます
            </Typography>

            {/* プレビュー */}
            <Paper
              variant="outlined"
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: themeSettings.backgroundColor,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, color: themeSettings.textColor }}>
                プレビュー
              </Typography>
              <Box
                sx={{
                  backgroundColor: themeSettings.headerBackgroundColor,
                  color: themeSettings.headerTextColor,
                  p: 1.5,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography variant="body2" fontWeight="bold">ヘッダー</Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: themeSettings.categoryBackgroundColor,
                  color: themeSettings.categoryTextColor,
                  p: 1,
                  borderRadius: 1,
                  mb: 2,
                  display: 'inline-block',
                }}
              >
                <Typography variant="body2">カテゴリ</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: themeSettings.textColor, mb: 2 }}>
                本文テキスト - 商品説明などに使われます
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: themeSettings.buttonColor,
                  color: themeSettings.buttonTextColor,
                  '&:hover': {
                    backgroundColor: themeSettings.buttonColor,
                    opacity: 0.9,
                  },
                }}
              >
                ボタン
              </Button>
            </Paper>

            <Grid container spacing={2}>
              {/* 基本カラー */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  基本カラー
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="背景色"
                  value={themeSettings.backgroundColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, backgroundColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.backgroundColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, backgroundColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="テキスト色"
                  value={themeSettings.textColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, textColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.textColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, textColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="プライマリカラー"
                  value={themeSettings.primaryColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, primaryColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.primaryColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, primaryColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* ヘッダーカラー */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                  ヘッダーカラー
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ヘッダー背景色"
                  value={themeSettings.headerBackgroundColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, headerBackgroundColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.headerBackgroundColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, headerBackgroundColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ヘッダー文字色"
                  value={themeSettings.headerTextColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, headerTextColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.headerTextColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, headerTextColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* ボタンカラー */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                  ボタンカラー
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ボタン背景色"
                  value={themeSettings.buttonColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, buttonColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.buttonColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, buttonColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ボタン文字色"
                  value={themeSettings.buttonTextColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, buttonTextColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.buttonTextColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, buttonTextColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* カテゴリカラー */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                  カテゴリカラー
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="カテゴリ背景色"
                  value={themeSettings.categoryBackgroundColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, categoryBackgroundColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.categoryBackgroundColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, categoryBackgroundColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="カテゴリ文字色"
                  value={themeSettings.categoryTextColor}
                  onChange={(e) => setThemeSettings({ ...themeSettings, categoryTextColor: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <input
                          type="color"
                          value={themeSettings.categoryTextColor}
                          onChange={(e) => setThemeSettings({ ...themeSettings, categoryTextColor: e.target.value })}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 保存ボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? '保存中...' : '設定を保存'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default MobileOrderSettings;
